import { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, MonitorPlay, Minus, Plus } from "lucide-react";
import {
  buildSlides,
  slideFooter,
  slideMetrics,
  SLIDE_W_PT,
  SLIDE_H_PT,
  FONT_OPTIONS,
  MIN_FONT_PT,
  MAX_FONT_PT,
  DEFAULT_FONT,
  DEFAULT_FONT_PT,
  MIN_TRANSITION_MS,
  MAX_TRANSITION_MS,
  DEFAULT_TRANSITION_MS,
} from "@/lib/slides";

const THUMB_W = 176;
const SCALE = THUMB_W / SLIDE_W_PT;

export function SlideFilmstrip({ segments, font = DEFAULT_FONT, fontSize = DEFAULT_FONT_PT, transitionMs = DEFAULT_TRANSITION_MS, onStyleChange, onPresentFrom }) {
  const [open, setOpen] = useState(true);
  const metrics = slideMetrics(fontSize);
  const slides = useMemo(() => buildSlides(segments || [], fontSize), [segments, fontSize]);
  const pt = (v) => `${v * SCALE}px`;

  return (
    <div className="no-print border-t border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] mt-2" data-testid="slide-filmstrip">
      <div className="flex items-center justify-between py-3 gap-4 flex-wrap">
        <button className="flex items-center gap-2 text-left" onClick={() => setOpen((o) => !o)} data-testid="slide-filmstrip-toggle">
          <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
            Slide preview · {slides.length} {slides.length === 1 ? "slide" : "slides"}
          </span>
          {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronUp className="h-4 w-4 text-muted-foreground" />}
        </button>

        {onStyleChange && (
          <div className="flex items-center gap-2" data-testid="slide-style-controls">
            <select
              value={font}
              onChange={(e) => onStyleChange({ slide_font: e.target.value })}
              className="h-8 rounded-full border border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] bg-transparent px-3 text-xs"
              title="Slide font"
              data-testid="slide-font-select"
            >
              {FONT_OPTIONS.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
            <div className="flex items-center gap-1 border border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] rounded-full h-8 px-1">
              <button
                className="h-6 w-6 rounded-full hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-30 flex items-center justify-center"
                disabled={fontSize <= MIN_FONT_PT}
                onClick={() => onStyleChange({ slide_font_size: Math.max(MIN_FONT_PT, fontSize - 2) })}
                title="Smaller slide text"
                data-testid="slide-font-decrease"
              >
                <Minus className="h-3 w-3" />
              </button>
              <span className="font-mono text-xs w-10 text-center" data-testid="slide-font-size-value">{fontSize} pt</span>
              <button
                className="h-6 w-6 rounded-full hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-30 flex items-center justify-center"
                disabled={fontSize >= MAX_FONT_PT}
                onClick={() => onStyleChange({ slide_font_size: Math.min(MAX_FONT_PT, fontSize + 2) })}
                title="Larger slide text"
                data-testid="slide-font-increase"
              >
                <Plus className="h-3 w-3" />
              </button>
            </div>
            <div className="flex items-center gap-1 border border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] rounded-full h-8 px-1" title="Fade time between slides" data-testid="slide-transition-controls">
              <button
                className="h-6 w-6 rounded-full hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-30 flex items-center justify-center"
                disabled={transitionMs <= MIN_TRANSITION_MS}
                onClick={() => onStyleChange({ slide_transition_ms: Math.max(MIN_TRANSITION_MS, transitionMs - 250) })}
                title="Faster fade"
                data-testid="slide-transition-decrease"
              >
                <Minus className="h-3 w-3" />
              </button>
              <span className="font-mono text-xs w-14 text-center" data-testid="slide-transition-value">
                {(transitionMs / 1000).toFixed(2)}s fade
              </span>
              <button
                className="h-6 w-6 rounded-full hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-30 flex items-center justify-center"
                disabled={transitionMs >= MAX_TRANSITION_MS}
                onClick={() => onStyleChange({ slide_transition_ms: Math.min(MAX_TRANSITION_MS, transitionMs + 250) })}
                title="Slower fade"
                data-testid="slide-transition-increase"
              >
                <Plus className="h-3 w-3" />
              </button>
            </div>
          </div>
        )}
      </div>

      {open && (
        <div className="pb-4">
          {slides.length === 0 ? (
            <p className="text-sm text-muted-foreground pb-2" data-testid="slide-filmstrip-empty">
              Add segments with text to preview the slides.
            </p>
          ) : (
            <div className="flex gap-3 overflow-x-auto pb-2">
              {slides.map((slide, i) => (
                <button
                  key={i}
                  onClick={() => onPresentFrom(i)}
                  title={`Present from slide ${i + 1}`}
                  className="group relative shrink-0 rounded-md overflow-hidden border border-black/20 hover:border-[var(--brand-crimson)] transition-colors"
                  style={{ width: THUMB_W, height: SLIDE_H_PT * SCALE }}
                  data-testid={`slide-thumb-${i}`}
                >
                  <div className="absolute inset-0 bg-black" />
                  <div className="absolute overflow-hidden" style={{ left: pt(72), right: pt(72), top: pt(64), bottom: pt(96) }}>
                    <div className="h-full flex flex-col justify-center">
                      {slide.lines.length === 0 ? (
                        <p className="text-white text-center" style={{ fontFamily: font, fontSize: pt(metrics.bodyPt), lineHeight: 1.25 }}>
                          {slide.title}
                        </p>
                      ) : (
                        slide.lines.map((line, j) =>
                          line.text === "" ? (
                            <div key={j} style={{ height: pt(metrics.bodyPt * 0.6) }} />
                          ) : (
                            <p
                              key={j}
                              className="text-white text-left break-words"
                              style={{ fontFamily: font, fontSize: pt(line.label ? metrics.labelPt : metrics.bodyPt), lineHeight: 1.25 }}
                            >
                              {line.text}
                            </p>
                          )
                        )
                      )}
                    </div>
                  </div>
                  <div
                    className="absolute flex items-center justify-between text-white/45 uppercase"
                    style={{ left: pt(48), right: pt(48), bottom: pt(28), fontSize: "6px" }}
                  >
                    <span className="truncate pr-1">{slideFooter(slide)}</span>
                    <span className="shrink-0">{i + 1}</span>
                  </div>
                  <div className="absolute inset-0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                    <MonitorPlay className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
